import { useFormikContext } from "formik";
import React, { FC, useState } from 'react';
import { DEF_PG_SIZE_SUB_MIN } from "../../../../constants/maximum-page-size";
import { LOCATION_COMMANDS, SITE_COMMANDS, ZONE_COMMANDS } from "../../../../constants/widget-constants";
import { TranslationKeys } from '../../../../contexts/TranslationKeys';
import { useLocale } from '../../../../contexts/localeContext';
import { TableContextProvider } from "../../../../contexts/table/TableContextProvider";
import doorColumns from '../../../../models/ColumnDefinitions/DoorColumns';
import siteColumns from '../../../../models/ColumnDefinitions/SiteColumns';
import zoneColumns from "../../../../models/ColumnDefinitions/ZoneColumns";
import { ListQuery } from '../../../../models/ListQuery';
import { Lookup } from '../../../../models/Lookup';
import LookupTypes from '../../../../models/LookupTypes';
import ControllerSearch from '../../../../models/search/ControllerSearch';
import doorSearch from '../../../../models/search/DoorSearch';
import { SearchTerm } from '../../../../models/search/SearchTerm';
import { default as SiteSearch, default as siteSearch } from '../../../../models/search/SiteSearch';
import TimeZoneSearch from '../../../../models/search/TimeZoneSearch';
import { default as ZoneSearch, default as zoneSearch } from "../../../../models/search/ZoneSearch";
import { TableColumn } from '../../../../models/table/TableColumn';
import { getControllerNames } from '../../../../services/controllerService';
import { getDoors } from '../../../../services/doorService';
import { getSiteNamesWithPagination, getSites } from '../../../../services/siteService';
import { getTimeZones } from '../../../../services/timeZoneService';
import { getZoneNamesWithPagination, getZones } from '../../../../services/zoneService';
import TableTab from "../../../tabs/table-tab";

interface Props {
    onBack: (event: any) => void;
    onNext: (event: any) => void;
    setSideBarStepDescription: (discription: string) => void;
}

interface TableContent {
    dataMethod: (listParameters?: ListQuery) => Promise<any>;
    title: string;
    columns: TableColumn[];
    searchDescriptors: SearchTerm[];
    lookups: Lookup[];
}

type TableContents = {
    [key: string]: TableContent;
};

const ItemSelectStep: FC<Props> = ({ onBack, onNext, setSideBarStepDescription }) => {
    const { translate } = useLocale();
    const { values, setFieldValue } = useFormikContext<any>();
    const [configuration, setConfiguration] = useState<{nameoriginal: string, id: string} | undefined>(undefined);

    const tableContents: TableContents = {
        [SITE_COMMANDS]: {
            dataMethod: getSites,
            title: translate.byKey(TranslationKeys.site),
            columns: siteColumns,
            searchDescriptors: siteSearch,
            lookups: [{
                name: LookupTypes.timeZone,
                values: [],
                dataMethod: getTimeZones,
                searchAccessor: TimeZoneSearch.find(item => item.accessor === "name")?.searchOnName
            }]
        },
        [ZONE_COMMANDS]: {
            dataMethod: getZones,
            title: translate.byKey(TranslationKeys.zone),
            columns: zoneColumns,
            searchDescriptors: zoneSearch,
            lookups: [
                {
                    name: LookupTypes.zoneBySiteId,
                    values: [],
                    dataMethod: getSiteNamesWithPagination,
                    searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
                },
                {
                    name: "controller",
                    values: [],
                    dataMethod: getControllerNames,
                    searchAccessor: ControllerSearch.find(item => item.accessor === "name")?.searchOnName
                }
            ]
        },
        [LOCATION_COMMANDS]: {
            dataMethod: getDoors,
            title: translate.byKey(TranslationKeys.door),
            columns: doorColumns,
            searchDescriptors: doorSearch,
            lookups: [
                {
                    name: LookupTypes.zoneName,
                    values: [],
                    dataMethod: getZoneNamesWithPagination,
                    searchAccessor: ZoneSearch.find(item => item.accessor === "name")?.searchOnName
                },
                {
                    name: LookupTypes.zoneController,
                    values: [],
                    dataMethod: getControllerNames,
                    searchAccessor: ControllerSearch.find(item => item.accessor === "name")?.searchOnName
                },
                {
                    name: LookupTypes.zoneSite,
                    values: [],
                    dataMethod: getSiteNamesWithPagination,
                    searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
                }
            ]
        }
    }

    const onLocalNext = (event: any) => {
        if (!!configuration) {
            onNext({ ...event });
            setFieldValue("configurationId", configuration.id);
            setFieldValue("configurationName", configuration.nameoriginal);
            setSideBarStepDescription(configuration.nameoriginal);
        }
    }

    const itemSelected = (selectedItem: any) => {
        if (selectedItem) {
            setConfiguration( () => {
                return {
                    nameoriginal: selectedItem.nameOriginal,
                    id: selectedItem.id
                }
            });
        }
    }

    const getColumnDescriptor = (): TableColumn[] => {
        return tableContents[values.filterBy].columns;
    };

    const getSearchDescriptor = (): SearchTerm[] => {
        return tableContents[values.filterBy].searchDescriptors;
    };

    const getDataMethod = () => {
        return tableContents[values.filterBy].dataMethod;
    };

    const getTitle = (): string => {
        return translate.byKey(tableContents[values.filterBy].title);
    };

    const getLookups = (): Lookup[] => {
        return tableContents[values.filterBy].lookups;
    }

    return (<div className="pt-3 pr-3 pl-3 h-full" style={{ height: "100%", overflow: "auto" }}>
        <TableContextProvider
            dataMethod={getDataMethod()}
            tableDescriptor={getColumnDescriptor()}
            searchDescriptor={getSearchDescriptor()}
            useTable
            usePagination
            useFilter
            useSearch
            useSort
            maxPageSize={DEF_PG_SIZE_SUB_MIN}
            lookup={getLookups()}>
            <TableTab
                title={getTitle()}
                selectRows={false}
                onRowClicked={itemSelected}
                selectedIds={[]}
                style={{ maxHeight: "calc(100% - 70px)" }}
                cyName="itemSelect"
            />
        </TableContextProvider>

        <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
            <div className="flex flex-row mt-4">
                <button
                    className="py-1 px-5 secondary secondary-inverse"
                    id="backButton"
                    type="button" onClick={onBack} >{translate.byKey(TranslationKeys.back)}</button>
            </div>
            <div className="flex flex-row justify-end mt-4 pl-3">
                <button
                    className={`py-1 px-5 secondary ${!!configuration ? 'secondary-enabled' : 'secondary-disabled cursor-not-allowed'}`}
                    type="button"
                    id="itemSelectNextButton"
                    onClick={e => onLocalNext(e)}>{translate.byKey(TranslationKeys.next)}</button>
            </div>
        </div>
    </div>)
}

export default ItemSelectStep;